// A question step in a conditional ladder (Pre-IFR: "has your issue been resolved?").
//
// The step is aimed at the customer. What follows depends on the answer:
//   yes        the ladder stops
//   no         the next staff step goes out: at once, or after a wait
//   no answer  a reminder goes out; after the last reminder with no answer, the ladder closes
//
// These helpers hold the question's settings in the units the editor shows and convert them to
// what the API stores. The rules mirror apps/api/ladder_questions.py (the server still rejects
// anything bad; this only says so before a round trip).

import { MAX_RUNG_SECONDS, splitDelay, toSeconds } from "./slaLadder";

export const MAX_QUESTION_SECONDS = 7 * 24 * 60 * 60;
export const MAX_REMINDERS = 5;

// Matches ladder_questions.DEFAULTS.
export const QUESTION_DEFAULTS = {
  no_delay_seconds: 0,
  no_answer_wait_seconds: 600,
  max_reminders: 1,
  reminder_template: "",
};

/** The editor's form for a question's stored settings (missing keys take the defaults). */
export function questionFromSettings(settings) {
  const s = { ...QUESTION_DEFAULTS, ...(settings || {}) };
  const noDelay = splitDelay(s.no_delay_seconds);
  const wait = splitDelay(s.no_answer_wait_seconds);
  return {
    noNow: Number(s.no_delay_seconds) === 0,
    noAmount: Number(s.no_delay_seconds) === 0 ? 1 : noDelay.amount,
    noUnit: Number(s.no_delay_seconds) === 0 ? "m" : noDelay.unit,
    waitAmount: wait.amount,
    waitUnit: wait.unit,
    reminders: s.max_reminders,
    reminderTemplate: s.reminder_template || "",
  };
}

/** What the API stores for a question as edited. "Immediately" is a wait of 0. */
export function settingsFromQuestion(q) {
  return {
    no_delay_seconds: q.noNow ? 0 : toSeconds({ amount: q.noAmount, unit: q.noUnit }),
    no_answer_wait_seconds: toSeconds({ amount: q.waitAmount, unit: q.waitUnit }),
    max_reminders: Number(q.reminders),
    reminder_template: (q.reminderTemplate || "").trim(),
  };
}

const isBlank = (value) => value === "" || value === null || value === undefined || String(value).trim() === "";

/** Why this question cannot be saved, or "" if it can. */
export function questionProblem(q) {
  const wait = Number(q.waitAmount);
  if (!Number.isFinite(wait) || wait <= 0) return "The wait for an answer must be more than zero.";
  const settings = settingsFromQuestion(q);
  if (settings.no_answer_wait_seconds > MAX_QUESTION_SECONDS) return "The wait for an answer can't be more than 7 days.";
  if (!q.noNow) {
    // An empty box is not "0": it would silently mean "immediately".
    if (isBlank(q.noAmount)) return "Enter the wait after a 'no', or choose Immediately.";
    const after = Number(q.noAmount);
    if (!Number.isFinite(after) || after < 0) return "The wait after a 'no' can't be negative.";
    if (settings.no_delay_seconds > MAX_QUESTION_SECONDS) return "The wait after a 'no' can't be more than 7 days.";
  }
  // Same for reminders: an empty box must not read as "none".
  const reminders = Number(q.reminders);
  if (isBlank(q.reminders) || !Number.isInteger(reminders) || reminders < 0 || reminders > MAX_REMINDERS) {
    return `Reminders must be a whole number from 0 to ${MAX_REMINDERS}.`;
  }
  return "";
}

/** A customer step in a ladder that asks questions is a question. */
export const isQuestion = (rung) => rung.target_type === "customer";

/**
 * Keys of the staff steps that wait for a "no": the first step that is not a question after
 * each question, in the ladder's order. Mirrors ladder_questions.gate_of. `ordered` must
 * already be in the ladder's order (by delay).
 */
export function gatedStepKeys(ordered) {
  const gated = new Set();
  ordered.forEach((rung, i) => {
    if (!isQuestion(rung)) return;
    const next = ordered.slice(i + 1).find((later) => !isQuestion(later));
    if (next) gated.add(next.key);
  });
  return gated;
}

/** The staff step a question's "no" goes to, or undefined when nothing follows it. */
export function stepAfterQuestion(ordered, question) {
  const at = ordered.findIndex((r) => r.key === question.key);
  return ordered.slice(at + 1).find((later) => !isQuestion(later));
}

export { MAX_RUNG_SECONDS };
